/**
 * Tooth capping (V7 preprocessing).
 *
 * Closes the root opening of an individual tooth mesh by:
 *   1. Walking each boundary loop of the tooth half-edge mesh.
 *   2. Snapping each boundary vertex onto the gum surface (BVH closestPointToPoint),
 *      in world-space, then converting back to tooth-local. The gum mesh itself is
 *      never modified.
 *   3. Adding a centroid vertex (also snapped to the gum) and fan-triangulating
 *      the cap so the result is a watertight tooth shell.
 *
 * Manifoldness: by forcing boundary vertices onto the gum surface, the union of
 *   (capped tooth volume) and (gum volume) shares its interface only at coincident
 *   surface points — the SDF sampler treats the union as a single solid because we
 *   sample distance, not topology.
 *
 * Fallbacks:
 *   - If any boundary vertex would have to stretch further than `maxSnapDistance`
 *     to reach the gum (e.g. after an aggressive correction), or the BVH query
 *     fails, we build a flat cap on the loop's best-fit plane (Newell's method)
 *     using the *original* loop positions. This keeps the cap watertight and away
 *     from the gum.
 *
 * Insertion axis is irrelevant here — capping is computed once per tooth at
 *   construction time, in tooth-local coordinates. The cap moves rigidly with
 *   any later correction.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Matrix4,
  Mesh,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import type { MeshBVH } from 'three-mesh-bvh';
import type { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import type HalfEdgeMesh from '@modules/three/common/halfEdgeMesh/HalfEdgeMesh';

const DEFAULT_MAX_SNAP_DISTANCE = 2;
const MIN_EDGE_LENGTH_SQ = 1e-10;

type ClosestPointInfo = Parameters<MeshBVH['closestPointToPoint']>[1];
type GumTransforms = {
  toothMatrixWorld: Matrix4;
  toothInverseWorld: Matrix4;
  gumMatrixWorld: Matrix4;
  gumInverseWorld: Matrix4;
};

export type ToothCapFallback = 'none' | 'flat' | 'partial';

export interface ToothCappingMetadata {
  loopsProcessed: number;
  capsAdded: number;
  trianglesAdded: number;
  maxSnapStretchMm: number;
  fallbackUsed: ToothCapFallback;
}

export interface ToothCappingResult {
  geometry: BufferGeometry;
  metadata: ToothCappingMetadata;
}

export interface ToothCappingOptions {
  /** Max permitted snap distance per boundary vertex (mm). Above this, flat cap fallback kicks in for that loop. */
  maxSnapDistance?: number;
  /** Suppress `[ToothCap]` console logs. */
  quiet?: boolean;
}

/**
 * Build a watertight capped version of a tooth's geometry, anchored to the gum surface.
 *
 * @param tooth The (open at the root) tooth half-edge mesh. Geometry is read in tooth-local space.
 * @param gumMesh The gum mesh providing the closest-point target surface. Not modified.
 * @returns A new BufferGeometry in tooth-local space with the original tooth tris plus cap fans.
 */
export function capToothToGum(
  tooth: HalfEdgeMesh,
  gumMesh: Mesh,
  options: ToothCappingOptions = {},
): ToothCappingResult {
  const maxSnapDistance = options.maxSnapDistance ?? DEFAULT_MAX_SNAP_DISTANCE;
  const quiet = options.quiet ?? false;

  tooth.updateMatrixWorld(true);
  gumMesh.updateMatrixWorld(true);

  const gumBVH = gumMesh.geometry.boundsTree;
  const transforms = buildTransforms(tooth, gumMesh);
  const { positions, indices } = copySourceGeometry(tooth);

  const loops = tooth.getBorderLoops();
  const metadata: ToothCappingMetadata = {
    loopsProcessed: 0,
    capsAdded: 0,
    trianglesAdded: 0,
    maxSnapStretchMm: 0,
    fallbackUsed: 'none',
  };

  for (const loop of loops) {
    processLoop(loop, {
      tooth,
      gumBVH,
      transforms,
      positions,
      indices,
      metadata,
      maxSnapDistance,
      quiet,
    });
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex(new Uint32BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  if (!quiet) {
    console.info('[ToothCap] done', {
      loops: metadata.loopsProcessed,
      caps: metadata.capsAdded,
      trianglesAdded: metadata.trianglesAdded,
      maxStretchMm: Number(metadata.maxSnapStretchMm.toFixed(3)),
      fallback: metadata.fallbackUsed,
    });
  }

  return { geometry, metadata };
}

interface ProcessLoopContext {
  tooth: HalfEdgeMesh;
  gumBVH: MeshBVH | undefined;
  transforms: GumTransforms;
  positions: number[];
  indices: number[];
  metadata: ToothCappingMetadata;
  maxSnapDistance: number;
  quiet: boolean;
}

function processLoop(loop: Vertex[], ctx: ProcessLoopContext): void {
  if (loop.length < 3) return;
  ctx.metadata.loopsProcessed++;

  const renderIds = resolveLoopRenderIds(loop, ctx.tooth, ctx.quiet);
  if (!renderIds) return;

  const snapResult = trySnapLoop(loop, ctx.gumBVH, ctx.transforms, ctx.maxSnapDistance, ctx.quiet);

  if (snapResult.snapFailed) {
    const added = buildFlatCap(loop, renderIds, ctx.positions, ctx.indices);
    ctx.metadata.trianglesAdded += added;
    ctx.metadata.capsAdded++;
    if (ctx.metadata.fallbackUsed === 'none') ctx.metadata.fallbackUsed = 'flat';
    if (snapResult.maxStretch > ctx.metadata.maxSnapStretchMm) {
      ctx.metadata.maxSnapStretchMm = snapResult.maxStretch;
    }
    if (!ctx.quiet) {
      console.info('[ToothCap] flat-cap fallback used', {
        loopVertices: loop.length,
        maxStretch: snapResult.maxStretch,
      });
    }
    return;
  }

  if (snapResult.maxStretch > ctx.metadata.maxSnapStretchMm) {
    ctx.metadata.maxSnapStretchMm = snapResult.maxStretch;
  }
  writeSnappedBoundary(renderIds, snapResult.snappedLocal, ctx.positions);
  const centroidLocal = computeCentroid(snapResult.snappedLocal);
  snapCentroidToGum(centroidLocal, ctx.gumBVH, ctx.transforms);
  const centroidIdx = ctx.positions.length / 3;
  ctx.positions.push(centroidLocal.x, centroidLocal.y, centroidLocal.z);
  ctx.metadata.trianglesAdded += fanTriangulate(renderIds, snapResult.snappedLocal, centroidIdx, ctx.indices);
  ctx.metadata.capsAdded++;
}

function resolveLoopRenderIds(loop: Vertex[], tooth: HalfEdgeMesh, quiet: boolean): number[] | null {
  const renderIds: number[] = [];
  for (const vertex of loop) {
    const id = tooth.halfEdges.vertexToRenderId.get(vertex);
    if (id === undefined) {
      if (!quiet) console.warn('[ToothCap] skipping loop with missing render ids');
      return null;
    }
    renderIds.push(id);
  }
  return renderIds;
}

interface SnapResult {
  snappedLocal: Vector3[];
  maxStretch: number;
  snapFailed: boolean;
}

function trySnapLoop(
  loop: Vertex[],
  gumBVH: MeshBVH | undefined,
  transforms: GumTransforms,
  maxSnapDistance: number,
  quiet: boolean,
): SnapResult {
  const snappedLocal = new Array<Vector3>(loop.length);
  if (!gumBVH) {
    return { snappedLocal, maxStretch: 0, snapFailed: true };
  }

  const tmpWorld = new Vector3();
  const tmpGumLocal = new Vector3();
  const hitPoint = new Vector3();
  let maxStretch = 0;

  for (let i = 0; i < loop.length; i++) {
    const localPos = loop[i].position;
    tmpWorld.copy(localPos).applyMatrix4(transforms.toothMatrixWorld);
    tmpGumLocal.copy(tmpWorld).applyMatrix4(transforms.gumInverseWorld);

    const hitInfo: ClosestPointInfo = { point: hitPoint, distance: 0, faceIndex: 0 };
    try {
      gumBVH.closestPointToPoint(tmpGumLocal, hitInfo);
    } catch (error) {
      if (!quiet) console.warn('[ToothCap] BVH query failed, falling back to flat cap', error);
      return { snappedLocal, maxStretch, snapFailed: true };
    }

    if (hitInfo.distance > maxStretch) maxStretch = hitInfo.distance;
    if (hitInfo.distance > maxSnapDistance) {
      return { snappedLocal, maxStretch, snapFailed: true };
    }

    snappedLocal[i] = hitPoint
      .clone()
      .applyMatrix4(transforms.gumMatrixWorld)
      .applyMatrix4(transforms.toothInverseWorld);
  }

  return { snappedLocal, maxStretch, snapFailed: false };
}

function writeSnappedBoundary(renderIds: number[], snappedLocal: Vector3[], positions: number[]): void {
  for (let i = 0; i < renderIds.length; i++) {
    const renderId = renderIds[i];
    positions[renderId * 3 + 0] = snappedLocal[i].x;
    positions[renderId * 3 + 1] = snappedLocal[i].y;
    positions[renderId * 3 + 2] = snappedLocal[i].z;
  }
}

function buildTransforms(tooth: HalfEdgeMesh, gumMesh: Mesh): GumTransforms {
  const toothMatrixWorld = tooth.matrixWorld.clone();
  const gumMatrixWorld = gumMesh.matrixWorld.clone();
  return {
    toothMatrixWorld,
    toothInverseWorld: new Matrix4().copy(toothMatrixWorld).invert(),
    gumMatrixWorld,
    gumInverseWorld: new Matrix4().copy(gumMatrixWorld).invert(),
  };
}

function copySourceGeometry(tooth: HalfEdgeMesh): { positions: number[]; indices: number[] } {
  const sourceGeom = tooth.geometry;
  const sourcePos = sourceGeom.getAttribute('position') as BufferAttribute;
  const sourceIdx = sourceGeom.getIndex();
  if (!sourcePos) {
    throw new Error('capToothToGum: tooth geometry has no position attribute.');
  }

  const positions: number[] = new Array(sourcePos.count * 3);
  for (let i = 0; i < sourcePos.count; i++) {
    positions[i * 3 + 0] = sourcePos.getX(i);
    positions[i * 3 + 1] = sourcePos.getY(i);
    positions[i * 3 + 2] = sourcePos.getZ(i);
  }

  const indices: number[] = [];
  if (sourceIdx) {
    for (let i = 0; i < sourceIdx.count; i++) {
      indices.push(sourceIdx.getX(i));
    }
  } else {
    for (let i = 0; i < sourcePos.count; i++) {
      indices.push(i);
    }
  }

  return { positions, indices };
}

function computeCentroid(points: Vector3[]): Vector3 {
  const centroid = new Vector3();
  for (const p of points) centroid.add(p);
  centroid.multiplyScalar(1 / points.length);
  return centroid;
}

function snapCentroidToGum(
  centroidLocal: Vector3,
  gumBVH: MeshBVH | undefined,
  transforms: GumTransforms,
): void {
  if (!gumBVH) return;
  const worldPoint = centroidLocal.clone().applyMatrix4(transforms.toothMatrixWorld);
  const gumLocal = worldPoint.applyMatrix4(transforms.gumInverseWorld);
  const hitPoint = new Vector3();
  const hitInfo: ClosestPointInfo = { point: hitPoint, distance: 0, faceIndex: 0 };
  try {
    gumBVH.closestPointToPoint(gumLocal, hitInfo);
  } catch {
    return;
  }
  centroidLocal.copy(hitPoint).applyMatrix4(transforms.gumMatrixWorld).applyMatrix4(transforms.toothInverseWorld);
}

function fanTriangulate(
  renderIds: number[],
  snappedLocal: Vector3[],
  centroidIdx: number,
  indices: number[],
): number {
  let added = 0;
  for (let i = 0; i < renderIds.length; i++) {
    const a = renderIds[i];
    const b = renderIds[(i + 1) % renderIds.length];
    if (a === b) continue;
    const pa = snappedLocal[i];
    const pb = snappedLocal[(i + 1) % renderIds.length];
    if (pa.distanceToSquared(pb) < MIN_EDGE_LENGTH_SQ) continue;
    indices.push(centroidIdx, a, b);
    added++;
  }
  return added;
}

/**
 * Flat-cap fallback: centroid-fan over the loop's original positions. We don't
 * snap to the gum (the snap is what failed), so the cap lives slightly off the
 * gum surface but is still watertight — which is all the SDF sampler requires.
 */
function buildFlatCap(
  loop: Vertex[],
  renderIds: number[],
  positions: number[],
  indices: number[],
): number {
  const centroid = new Vector3();
  for (const v of loop) centroid.add(v.position);
  centroid.multiplyScalar(1 / loop.length);

  const centroidIdx = positions.length / 3;
  positions.push(centroid.x, centroid.y, centroid.z);

  let added = 0;
  for (let i = 0; i < loop.length; i++) {
    const a = renderIds[i];
    const b = renderIds[(i + 1) % loop.length];
    if (a === b) continue;
    const pa = loop[i].position;
    const pb = loop[(i + 1) % loop.length].position;
    if (pa.distanceToSquared(pb) < MIN_EDGE_LENGTH_SQ) continue;
    indices.push(centroidIdx, a, b);
    added++;
  }
  return added;
}
